import React, { FC, useEffect } from 'react';
import { style, stylesheet } from 'typestyle';
import {
  Button,
  ButtonsRow,
  Gold10,
  Gold80,
  Red0,
  Red90,
  typeCompact12RegularProperties,
  typeCompact14RegularProperties,
  White,
} from '@traceair/tds';
import { trackAction } from '../../../libs/analytics/ActionTracking';
import { useAppStore } from '../../../store/appStore';
import { useDocumentsStore } from '../../../store/documentsStore';
import AlertIcon from '../../../images/components/AlertIcon';
import { CoaDocument, DocType, DocumentStatus, PilotDoc } from '../../../types';
import { trueDaysDiff } from '../../../libs/trueDateDiff';

const DocAlertPopup: FC = () => {
  const hideWindow = useAppStore((state) => state.hideWindow);
  const setSelectedSidebarPanel = useAppStore((state) => state.setSelectedSidebarPanel);

  const hasNeedAttention = useDocumentsStore((state) => state.computed.hasNeedAttention());
  const missedDocTypes = useDocumentsStore((state) => state.missedDocTypes);
  const needAttentionDocs = useDocumentsStore((state) => state.needAttentionDocs);
  const needAttentionCoaDocs = useDocumentsStore((state) => state.needAttentionCoaDocs);

  const content = getContent(missedDocTypes, needAttentionDocs, needAttentionCoaDocs);

  function handleCloseClick() {
    trackAction('Close on documents alert button click', null);
    hideWindow();
  }

  function handleGoToDocs() {
    trackAction('Go to Documents on documents alert button click', null);
    setSelectedSidebarPanel('DOCUMENTS');
    hideWindow();
  }

  useEffect(() => {
    if (!hasNeedAttention) hideWindow();
  }, []);

  return (
    <div className={css.root}>
      <div className={css.content}>
        <div className={css.alertContainer} style={{ backgroundColor: content.isRed ? Red0 : Gold10 }}>
          <AlertIcon color={content.isRed ? Red90 : Gold80} />
        </div>
        <div className={css.title}>{content.title}</div>
        <div className={css.description}>{content.desc}</div>
      </div>
      <ButtonsRow className={style({ gap: 8 })}>
        <Button className={css.closeButton} onClick={handleCloseClick} text="Close" style="secondary" />
        <Button onClick={handleGoToDocs} text="Go to Documents" />
      </ButtonsRow>
    </div>
  );
};

const css = stylesheet({
  root: {
    width: 244,
    padding: 16,
    boxSizing: 'border-box',
    backgroundColor: White,
    borderRadius: 16,
    boxShadow: '0px 3px 20px 0px rgba(0, 0, 0, 0.20), 0px 6px 40px 0px rgba(0, 0, 0, 0.40)',
  },
  content: {
    padding: '16px 16px 32px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'center',
  },
  alertContainer: {
    width: 48,
    height: 48,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
  },
  title: {
    ...typeCompact14RegularProperties,
    margin: '12px 0 8px 0',
  },
  description: {
    ...typeCompact12RegularProperties,
  },
  closeButton: {
    paddingLeft: 14,
    paddingRight: 14,
  },
});

export default DocAlertPopup;

function getContent(
  missedDocs: DocType[],
  docs: PilotDoc[],
  coaDocs: CoaDocument[],
): { isRed: boolean; title: string; desc: string } {
  const content = {
    isRed: true,
    title: 'Some required documents need your attention',
    desc: 'Please update them asap on the Documents page',
  };

  // more than one need attention document
  // Temporarily disable all COA alerts https://app.asana.com/0/1207159525696167/1207332932503727/f
  // if (missedDocs.length + docs.length + coaDocs.length > 1) {
  if (missedDocs.length + docs.length > 1) {
    return content;
  }

  // only one non-coa is not uploaded
  if (missedDocs.length === 1) {
    setContentIsNotUploaded(missedDocs[0]);

    return content;
  }

  // other issue with only one non-coa doc
  if (docs.length === 1) {
    const doc = docs[0];

    if (doc.status === DocumentStatus.FIX_REQUESTED) {
      setContentFixNeeded(doc.type);

      return content;
    }

    if (doc.endDate) {
      setContentExpiring(doc.type, doc.endDate);

      return content;
    }
  }

  // issue with only one coa doc
  // Temporarily disable all COA alerts https://app.asana.com/0/1207159525696167/1207332932503727/f
  // if (coaDocs.length === 1) {
  //   const coaDoc = coaDocs[0].document;
  //   const coaDocType = DocType.CERTIFICATE_OF_AUTHORIZATION;

  //   if (coaDoc) {
  //     if (coaDoc.status === DocumentStatus.FIX_REQUESTED) {
  //       setContentFixNeeded(coaDocType);

  //       return content;
  //     }

  //     if (coaDoc.endDate) {
  //       setContentExpiring(coaDocType, coaDoc.endDate);

  //       return content;
  //     }
  //     // coa not uploaded
  //   } else {
  //     setContentIsNotUploaded(coaDocType);

  //     return content;
  //   }
  // }

  function setContentFixNeeded(docType: DocType) {
    content.title = `Your ${docType} need to be fixed`;
    content.desc = 'Please check it for accuracy on the Documents page';
  }

  function setContentExpiring(docType: DocType, endDate: Date) {
    const { daysDiff, daysDiffAbsStr } = trueDaysDiff(endDate);

    const isExpired = daysDiff < 0;
    const titleDaysStr =
      daysDiff < 0 ? 'is expired' : daysDiff === 0 ? 'is expiring today' : `will expire in ${daysDiffAbsStr}`;

    content.isRed = isExpired;
    content.title = `Your ${docType} ${titleDaysStr}`;
    content.desc = isExpired
      ? 'Please upload new version asap on the Documents page'
      : 'Please upload new version till expiring date on the Documents page';
  }

  function setContentIsNotUploaded(docType: DocType) {
    content.title = `Your ${docType} is not uploaded`;
    content.desc = 'Please upload document asap on the Documents page';
  }

  return content;
}
